import FTLStopHelpers from '@/utilities/FTLStopHelpers';

// Stop pinning (plan page)
// -----------------------------------------------------------------------------
// A dispatcher can "pin" a customer stop to lock it to the front of the route.
// Pinned stops must always form a contiguous PREFIX of the customer-stop
// sequence: the first customer stop can be pinned, the second can only be
// pinned once the first is, and so on. The initial warehouse pickup is not a
// customer stop, so it is never pinnable and is ignored by these rules.
//
// "Pinnable" == a customer DROPOFF stop. FTLStopHelpers.isCustomerStop already
// excludes the warehouse pickup, final returns (DROPOFF w/ returns.length),
// lunch, and the start/end bookends — but its PICKUP branch also matches
// mid-route return pickups (PICKUP w/ returns.length). Those aren't dropoffs and
// pinning them (forcing them to the front of the dropoff region) is not
// intended, so we additionally require type === 'DROPOFF'.

/**
 * Ordered list of stops eligible for pinning (customer dropoffs only), sorted by
 * their position in the route.
 * @param {object} route
 * @returns {Array<object>}
 */
export const getPinnableStops = (route) => {
    return FTLStopHelpers.getStopSequence(route?.stopsByRouteId).filter(
        (stop) => FTLStopHelpers.isCustomerStop(stop, route) && stop.type === 'DROPOFF'
    );
};

/**
 * Number of leading pinned stops that form a contiguous prefix of the pinnable
 * sequence. A pinned stop that sits after an unpinned one (a broken invariant)
 * is NOT counted here.
 * @param {Array<object>} pinnable - result of getPinnableStops
 * @returns {number}
 */
const leadingPinnedCount = (pinnable) => {
    let count = 0;
    for (const stop of pinnable) {
        if (stop.pinned) count += 1;
        else break;
    }
    return count;
};

/**
 * Per-stop pin state for rendering the pin control on a StopListItem.
 *
 * canPin is true only for the next un-pinned stop after the current prefix
 * (enforcing "all preceding stops must be pinned"). canUnpin is true for any
 * pinned stop — unpinning cascades to every pinned stop after it (see
 * getUnpinCascadeIds), so the prefix stays contiguous.
 *
 * @param {object} stop
 * @param {object} route
 * @returns {{ isPinnable: boolean, index: number, isPinned: boolean, canPin: boolean, canUnpin: boolean }}
 */
export const getStopPinState = (stop, route) => {
    const pinnable = getPinnableStops(route);
    const index = pinnable.findIndex((s) => s.stop_id === stop.stop_id);
    const isPinnable = index !== -1;
    const isPinned = !!stop.pinned;

    if (!isPinnable) {
        return { isPinnable: false, index: -1, isPinned, canPin: false, canUnpin: false };
    }

    const prefixCount = leadingPinnedCount(pinnable);

    return {
        isPinnable: true,
        index,
        isPinned,
        canPin: !isPinned && index === prefixCount,
        canUnpin: isPinned,
    };
};

/**
 * Stop ids to unpin when the user unpins `stop`: the stop itself plus every
 * pinned stop after it in the pinnable sequence. Keeps the pinned set a
 * contiguous prefix.
 * @param {object} stop
 * @param {object} route
 * @returns {Array<string>}
 */
export const getUnpinCascadeIds = (stop, route) => {
    const pinnable = getPinnableStops(route);
    const index = pinnable.findIndex((s) => s.stop_id === stop.stop_id);
    if (index === -1) return [stop.stop_id];
    return pinnable
        .slice(index)
        .filter((s) => s.pinned)
        .map((s) => s.stop_id);
};

/**
 * After a manual reorder, pinned stops must still form a contiguous prefix of
 * the customer-stop sequence. Given the NEW order, return the ids of pinned
 * stops that are no longer part of that leading run — their pin should be
 * dropped. Once the first un-pinned customer stop appears, every pinned stop
 * after it is a drop.
 *
 * @param {Array<object>} orderedStops - stops in their new order (pinned flags intact)
 * @param {object} route
 * @returns {Set<string>}
 */
export const getPinDropIds = (orderedStops, route) => {
    const pinnable = FTLStopHelpers.getStopSequence(orderedStops).filter(
        (s) => FTLStopHelpers.isCustomerStop(s, route) && s.type === 'DROPOFF'
    );
    const drops = new Set();
    let prefixBroken = false;
    for (const stop of pinnable) {
        if (!stop.pinned) {
            prefixBroken = true;
        } else if (prefixBroken) {
            drops.add(stop.stop_id);
        }
    }
    return drops;
};

/**
 * The contiguous leading run of pinned customer stops (P1..Pk). Ignores any
 * orphan pinned stop that sits after an unpinned one.
 * @param {object} route
 * @returns {Array<object>}
 */
export const getPinnedPrefix = (route) => {
    const pinnable = getPinnableStops(route);
    return pinnable.slice(0, leadingPinnedCount(pinnable));
};

/**
 * Index in `stops` where the pinned block should be inserted — the start of the
 * dropoff region: the first customer stop, falling back to the first final
 * return / end bookend, falling back to the end of the list.
 */
const dropoffRegionStart = (stops, route) => {
    let idx = stops.findIndex((s) => FTLStopHelpers.isCustomerStop(s, route));
    if (idx !== -1) return idx;
    idx = stops.findIndex((s) => FTLStopHelpers.isFinalDropoff(s) || s.end);
    return idx === -1 ? stops.length : idx;
};

/**
 * Re-assert the pinned prefix on a stop set returned by the optimizer.
 *
 * The optimizer is told which stops are pinned (`pinned_stop_ids`) and starts
 * its sub-problem at the last of them, so it already returns the prefix at the
 * front of the dropoff region and this is a no-op. It stays as a guard: a pin is
 * a promise to the dispatcher, so a response that puts a pinned stop elsewhere
 * gets corrected here rather than shipped to the driver.
 *
 * @param {object} args
 * @param {Array<object>} args.optimizedStops - stops returned by the optimizer
 * @param {Array<object>} args.pinnedPrefix - the pinned stops, in pinned order (P1..Pk)
 * @param {object} args.route
 * @returns {Array<object>} full stop set, ordering re-sequenced
 */
export const enforcePinnedPrefix = ({ optimizedStops, pinnedPrefix, route }) => {
    const seq = FTLStopHelpers.getStopSequence(optimizedStops);

    if (!pinnedPrefix?.length) {
        return seq;
    }

    const pinnedIds = new Set(pinnedPrefix.map((s) => s.stop_id));
    const rest = seq.filter((s) => !pinnedIds.has(s.stop_id));
    // Prefer the optimizer's copy of each pinned stop — it carries the scalars
    // the response wrote — and fall back to the route's when it is missing.
    const pinnedBlock = pinnedPrefix.map((pinned) => seq.find((s) => s.stop_id === pinned.stop_id) || pinned);

    const insertIdx = dropoffRegionStart(rest, route);

    const merged = [...rest.slice(0, insertIdx), ...pinnedBlock, ...rest.slice(insertIdx)];
    // Re-sequence by array position — NOT getStopSequence, which sorts on the
    // stale `ordering` field and would undo the pinned-block placement.
    return merged.map((stop, index) => ({ ...stop, ordering: index }));
};
